import { useEffect, useRef, useState } from "react";
import { HelpCircle, RotateCcw, PlayCircle } from "lucide-react";
import { useTour } from "./TourContext";

/**
 * Header-mounted "?" button that opens a small menu listing every tour
 * registered by the currently-mounted page (and any sibling pages that
 * also mounted on this route — usually just the active one). Clicking
 * an item replays that tour; "Reset all tours" clears every dismissal
 * so first-visit auto-start triggers again.
 */
export function TourLauncher() {
  const { registry, start, resetAllDismissals } = useTour();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const tours = Object.values(registry);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Help and guided tours"
        title="Guided tours"
        className="rounded-md p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
      >
        <HelpCircle className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-72 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg ring-1 ring-slate-900/5">
          <div className="border-b border-slate-100 bg-slate-50 px-3 py-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Guided tours
            </div>
            <div className="mt-0.5 text-[11px] text-slate-500">
              {tours.length === 0 ? "No tour registered for this page." : "Replay any tour any time."}
            </div>
          </div>
          {tours.length > 0 && (
            <ul className="max-h-72 overflow-auto py-1">
              {tours.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => {
                      start(t, { force: true });
                      setOpen(false);
                    }}
                    className="flex w-full items-start gap-2 px-3 py-2 text-left transition hover:bg-emerald-50"
                  >
                    <PlayCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-900">{t.title}</div>
                      {t.subtitle && (
                        <div className="truncate text-xs text-slate-500">{t.subtitle}</div>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="border-t border-slate-100 px-3 py-2">
            <button
              type="button"
              onClick={() => {
                resetAllDismissals();
                setOpen(false);
              }}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Reset all tours
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
